"use client";

import { useRouter } from "@faire/deprecated-retailer-visitor-shared/app/_lib/routing/useRouter";
import {
  useAssignSettingEnableBrandCentricSearch,
  useSettingEnableBrandCentricSearch,
} from "@faire/deprecated-retailer-visitor-shared/settings/useSettingEnableBrandCentricSearch";
import { SearchPageResultType } from "@faire/web-api--source/indigofair/data/SearchPageResultType";
import { useStrictLocalization } from "@faire/web/common/localization";
import { Column, Row } from "@faire/web/slate/Layout";
import { Typography } from "@faire/web/slate/Typography";
import { cn } from "@faire/web/tools/cn";
import { useSearchParams, usePathname } from "next/navigation";
import React, { useCallback, useEffect, useState } from "react";

const buildQueryParams = (
  pathname: string,
  searchParams: URLSearchParams,
  resultType: SearchPageResultType
) => {
  const newUrlSearchParams = new URLSearchParams(searchParams);

  // we don't carry filters or page number across result type toggles
  newUrlSearchParams.delete("filters");
  newUrlSearchParams.delete("page");
  newUrlSearchParams.set("resultType", resultType);
  const queryParams = new URLSearchParams(newUrlSearchParams);
  return `${pathname}?${queryParams.toString().replace(/\+/g, "%20")}`;
};

export const ResultTypeToggleGroup: React.FC = () => {
  const { strictLocalize } = useStrictLocalization();
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const isBrandCentricSearchEnabled = useSettingEnableBrandCentricSearch();

  const [selectedResultType, setSelectedResultType] =
    useState<SearchPageResultType>();

  useEffect(() => {
    const resultTypeParam = searchParams.get("resultType");
    if (resultTypeParam === SearchPageResultType.BRAND_RESULTS) {
      setSelectedResultType(SearchPageResultType.BRAND_RESULTS);
    } else if (resultTypeParam === SearchPageResultType.PRODUCT_RESULTS) {
      setSelectedResultType(SearchPageResultType.PRODUCT_RESULTS);
    } else {
      // Default to products if no valid resultType is found
      setSelectedResultType(SearchPageResultType.PRODUCT_RESULTS);
    }
  }, [searchParams]);

  const assignSettingEnableBrandCentricSearch =
    useAssignSettingEnableBrandCentricSearch();

  // TODO (FD-297341): implement prefetching on hover.
  // Also still need to handle transition states.
  const handleClick = useCallback(
    (resultType: SearchPageResultType) => {
      setSelectedResultType(resultType);
      router.push(buildQueryParams(pathname, searchParams, resultType));
    },
    [router, searchParams, pathname]
  );

  useEffect(() => {
    void assignSettingEnableBrandCentricSearch();
  }, [assignSettingEnableBrandCentricSearch]);

  if (!isBrandCentricSearchEnabled) {
    return null;
  }

  return (
    <Row
      data-test-id="result-type-toggle-group"
      className="bg-fs-border-muted mt-2 mb-2 ml-auto h-10 rounded-[20px]"
      align="end"
      gap={4}
    >
      <Column
        role="button"
        aria-pressed={
          selectedResultType === SearchPageResultType.PRODUCT_RESULTS
        }
        className={cn(
          "my-1 ml-1 h-8 cursor-pointer justify-center rounded-2xl px-4",
          selectedResultType === SearchPageResultType.PRODUCT_RESULTS
            ? "bg-fs-surface-primary"
            : "bg-fs-border-muted"
        )}
        justify="center"
        tabIndex={0}
        onClick={() => handleClick(SearchPageResultType.PRODUCT_RESULTS)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleClick(SearchPageResultType.PRODUCT_RESULTS);
          }
        }}
      >
        <Typography>
          {strictLocalize({
            id: "NcpOSd",
            defaultMessage: "Products",
            description: {
              text: "Text inside a button that changes the search results to products",
              img: "https://cdn.faire.com/fastly/fcb2f277ed3690bc0b2eccc37a799c7f070af91157b0f88b73fa95a38313b42d.png",
            },
          })}
        </Typography>
      </Column>
      <Column
        role="button"
        aria-pressed={selectedResultType === SearchPageResultType.BRAND_RESULTS}
        className={cn(
          "my-1 mr-1 h-8 cursor-pointer justify-center rounded-2xl px-4",
          selectedResultType === SearchPageResultType.BRAND_RESULTS
            ? "bg-fs-surface-primary"
            : "bg-fs-border-muted"
        )}
        justify="center"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleClick(SearchPageResultType.BRAND_RESULTS);
          }
        }}
        onClick={() => handleClick(SearchPageResultType.BRAND_RESULTS)}
      >
        <Typography>
          {strictLocalize({
            id: "BBZ7Rh",
            defaultMessage: "Brands",
            description: {
              text: "Text inside a button that changes the search results to brands",
              img: "https://cdn.faire.com/fastly/e8eae5c6181686c55ad20ffc60d7c548885c47ae2d25a387065e8847298724bc.png",
            },
          })}
        </Typography>
      </Column>
    </Row>
  );
};
